import Image from 'next/image'

import type { Address } from '@rechargeapps/storefront-client'
import type { RichTextField } from '@prismicio/client'
import _ from 'lodash'

import boxConfigurations from '@data/boxConfigurations.json'
import customizationData from '@data/customization.json'

import type { ShopifyBasicOrderShippingAddress } from '@lib/queries/orders/get-basic-orders-query'
import type { CustomizationSelectionType, SingleSelectionType } from '@lib/types/customization'

import { useEffect, useState } from 'react'
import { getProductSamplesWithCollectionLikeFormat } from '@lib/operations/product'
import { filterBonusGroups } from '@lib/customization/bonus-items'
import styles from '../styles/CustomizationModal.module.scss'
import CustomizationAddressBlock from './CustomizationAddressBlock'
import CustomizationReviewCard from './CustomizationReviewCard'
import SubmissionAlert from './SubmissionAlert'

type CustomizationReviewProps = {
	shippingInfo?: Address | ShopifyBasicOrderShippingAddress
	selections: CustomizationSelectionType[]
	bonusItemSelections?: SingleSelectionType[]
	updateStep: (step: number) => void
	submitSelections: () => void
	customizationType: 'NMC' | 'RMC'
	subscriptionProductSKU: string
	bonusItemConfig?: {
		tagText: string
		tagColor: string
		tooltipText?: RichTextField
	}
}

export default function CustomizationReview({
	shippingInfo,
	selections,
	bonusItemSelections = [],
	updateStep,
	submitSelections,
	customizationType,
	subscriptionProductSKU,
	bonusItemConfig
}: CustomizationReviewProps) {
	const boxConfiguration = boxConfigurations.find((config) =>
		config.product_skus.some((sku) => sku.sku === subscriptionProductSKU)
	)

	const allCustomizationSteps =
		customizationType === 'NMC'
			? boxConfiguration?.nmc.customization_groups
			: boxConfiguration?.rmc.customization_groups
	const customizationSteps = filterBonusGroups(allCustomizationSteps as any[])
	const additionalProducts = ((customizationType === 'NMC'
		? boxConfiguration?.nmc.additional_products
		: boxConfiguration?.rmc.additional_products) || []) as any[]

	const filteredAdditionalProducts = additionalProducts.filter(
		(product) => !product.hide && product.variant_id
	)

	const [additionalProductsToShow, setAdditionalProductsToShow] = useState<SingleSelectionType[]>(
		[]
	)

	useEffect(() => {
		const getAdditionalProducts = async () => {
			if (filteredAdditionalProducts.length > 0) {
				const additionalProductIds = filteredAdditionalProducts.map(
					(product) => `gid://shopify/ProductVariant/${product.variant_id}`
				)
				const productsDetails =
					await getProductSamplesWithCollectionLikeFormat(additionalProductIds)
				setAdditionalProductsToShow(
					additionalProductIds
						.map((id) => {
							const product = productsDetails.find((p) => p.sampleVariants.some((v) => v.id === id))
							const variant = product?.sampleVariants.find((v) => v.id === id)
							return variant ? { product, variant } : null
						})
						.filter((item): item is SingleSelectionType => item !== null)
				)
			}
		}
		getAdditionalProducts()
	}, [])

	if (!boxConfiguration) {
		console.error(`No box configuration found for SKU: ${subscriptionProductSKU}`)
		return <div>Error: No box configuration found</div>
	}

	const customizationMoreProductsMessage = customizationData.confirmation_page_message as any[]
	const customizationMoreProductsImage = customizationData.confirmation_page_message_image as any
	const customizationLandingPageFaq = customizationData.faq_link
	const customizationLandingPageSupport = customizationData.contact_support

	return (
		<div className="flex max-md:block gap-[60px] w-[100%] max-w-[900px] px-[30px]">
			{/* address */}
			<CustomizationAddressBlock shippingInfo={shippingInfo} className="hidden max-md:block" />
			<SubmissionAlert className="hidden max-md:block mb-4" />

			<ul className="gap-[16px] w-[60%] max-md:w-full mb-[24px]">
				{/* Bonus items first (only those not hidden from summary, e.g. free gift; inserts/box SKUs stay hidden) */}
				{bonusItemSelections
					.filter((sel) => !sel.hideFromSummary)
					.map((sel) => (
						<CustomizationReviewCard
							product={sel.product}
							variant={sel.variant}
							key={sel.variant.id}
							tagText={bonusItemConfig?.tagText}
							tagIcon={bonusItemConfig?.tooltipText ? 'info' : 'none'}
							tooltipText={bonusItemConfig?.tooltipText}
							onEdit={undefined}
						/>
					))}
				{/* Regular customization selections */}
				{customizationSteps &&
					_.zip(selections, customizationSteps).map(([selection, step], idx) =>
						selection?.selections && selection?.selections.length
							? selection?.selections.map((sel) => (
									<CustomizationReviewCard
										product={sel.product}
										variant={sel.variant}
										// eslint-disable-next-line react/no-array-index-key
										key={idx}
										onEdit={() => updateStep(idx)}
									/>
								))
							: false
					)}
				{filteredAdditionalProducts.map((addProd) => {
					const prodToShow = additionalProductsToShow.find((addP) =>
						addP.variant.id.endsWith(addProd.variant_id || '-')
					)
					if (!prodToShow) {
						return null
					}
					return (
						<CustomizationReviewCard
							product={prodToShow.product}
							variant={prodToShow.variant}
							key={prodToShow.variant.id}
							tagText={addProd.tag_text || undefined}
							tagIcon={addProd.tag_icon as 'none' | 'plus' | undefined}
						/>
					)
				})}
				{customizationMoreProductsMessage[0] && (
					<li className="customization-card w-auto text-left">
						<div
							className={`${styles.cardContent} grid grid-cols-[1.5fr,2.5fr] items-center bg-white shadow-bbx-card mb-[12px]`}
						>
							<div className="text-center relative w-full aspect-[1] bg-contain bg-center bg-no-repeat h-full max-w-[176px]">
								<Image
									src={`${customizationMoreProductsImage.url}&width=400`}
									className="w-full object-contain max-h-full"
									alt={customizationMoreProductsImage.alt || 'Birchbox Image'}
									layout="fill"
								/>
							</div>
							<div className="py-[24px] px-[24px]">
								<h4 className="font-bold">{customizationMoreProductsMessage[0].text}</h4>
							</div>
						</div>
					</li>
				)}
			</ul>

			<div className="w-[40%] max-md:w-full">
				{/* address */}
				<CustomizationAddressBlock shippingInfo={shippingInfo} className="max-md:hidden" />

				{/* out of stock message that sits above the disabled button */}
				{/* <div className="rounded-md bg-bbx-spark/10 flex p-4 w-full max-w-[800px] mt-4 text-left text-[16px] mb-2">
					<i className="fa-solid fa-bell-exclamation flex-none text-bbx-spark pl-1 pr-4 pt-1" />
					<p className="">
						<strong className="font-medium">Out of stock</strong>
						<br />
						<PrismicRichText field={customizationLandingPageOutOfStock} />
					</p>
				</div> */}

				{/* Todo: This button needs to be disabled if a product is out of stock */}
				<button
					className="button button-action w-full mb-2 max-md:hidden disabled:"
					type="button"
					onClick={submitSelections}
				>
					Confirm & Submit
				</button>
				<SubmissionAlert className="max-md:hidden mb-4" />
				<div className="text-center">
					<p className="font-bold">Need Help?</p>
					<p>
						<a
							className="underline"
							href={customizationLandingPageFaq}
							target="_blank"
							rel="noopener noreferrer"
						>
							FAQ{' '}
						</a>
						or{' '}
						<a
							className="underline"
							rel="noopener noreferrer"
							target="_blank"
							href={`mailto:${customizationLandingPageSupport}`}
						>
							Contact Support
						</a>
					</p>
				</div>
			</div>
		</div>
	)
}
